import { useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';

import useSWR from 'swr';
import QRCode from 'react-qr-code';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { ENDPOINTS } from '@/api/endpoints';
import { twoFactorEnable, twoFactorGenerateQRCode } from '@/api/two-factor-auth';
import { toast_error, toast_success } from '@/libs/toast-wrappers';
import { otpSchema } from '@/utils/zod-schemas';

import { BasicDrawer } from '@/components/ui/basic-drawer';

import { FetchStates } from '../fetch-states';
import { Button } from '../ui/button';
import { InputOTPSlotGroup } from '../ui/input-otp';
import { InputOTP } from '../ui/input-otp';
import { Label } from '../form/label';
import { Separator } from '../ui/separator';

// Google Authenticator download links
const GOOGLE_AUTHENTICATOR_LINKS = {
  ios: 'https://apps.apple.com/app/google-authenticator/id388497605',
  android: 'https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2',
};

const schema = z.object({ verification_code: otpSchema });
type EnableFormData = z.infer<typeof schema>;

export function TwoFactorAuthEnableForm() {
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();
  const { onFormClose } = useOutletContext<{
    onFormClose: () => Promise<void>;
  }>();

  const navigate = useNavigate();
  const {
    data,
    isLoading: isLoadingQR,
    isValidating: isValidatingQR,
  } = useSWR(ENDPOINTS.TWO_FACTOR_GENERATE, twoFactorGenerateQRCode, {
    revalidateOnFocus: false,
  });

  const { handleSubmit, control } = useForm<EnableFormData>({
    resolver: zodResolver(schema),
  });

  const onSubmit: SubmitHandler<EnableFormData> = async (data) => {
    setIsLoading(true);
    try {
      const response = await twoFactorEnable(data.verification_code);
      if (response.enabled) {
        await onFormClose();
        toast_success(t('two-factor-auth-enabled-success'));
        navigate(-1);
      }
    } catch {
      toast_error(t('two-factor-auth-enable-code-error'));
      setIsLoading(false);
    }
  };

  const onClose = () => {
    navigate(-1);
  };

  return (
    <BasicDrawer size="sm" open={true} onClose={onClose} title={t('two-factor-auth-enable-title')}>
      <FetchStates
        loading={isLoadingQR || isValidatingQR}
        empty={Boolean(!data?.qr_code)}
        loadingMessage={t('two-factor-auth-enable-loading')}
      >
        <div className="flex flex-col gap-4 justify-center items-center">
          <Label>{t('two-factor-auth-enable-qr-code')}</Label>
          <div className="flex border-2 border-gray-300 rounded-md p-3 w-fit mx-auto shadow-sm">
            <QRCode value={data?.qr_code ?? ''} />
          </div>

          {/* Google Authenticator download links */}
          <div className="text-center text-sm text-gray-600">
            <p className="mb-3">{t('two-factor-auth-download-app')}</p>
            <p className="mb-3">{t('two-factor-auth-download-google-auth')}</p>
            <div className="flex justify-center gap-3">
              <Button variant="outline" size="sm" href={GOOGLE_AUTHENTICATOR_LINKS.ios}>
                iOS
              </Button>
              <Button variant="outline" size="sm" href={GOOGLE_AUTHENTICATOR_LINKS.android}>
                Android
              </Button>
            </div>
          </div>
          <Separator />
          <form
            className="flex flex-col max-w-72 items-center gap-4"
            onSubmit={handleSubmit(onSubmit)}
          >
            <Label>{t('two-factor-auth-verify-description')}</Label>
            <Controller
              control={control}
              name="verification_code"
              render={({ field }) => (
                <InputOTP maxLength={6} {...field}>
                  <InputOTPSlotGroup />
                </InputOTP>
              )}
            />
            <Button
              disabled={isLoading || isLoadingQR}
              loading={isLoading}
              type="submit"
              className="w-full inline-block"
            >
              {t('two-factor-auth-enable-button')}
            </Button>
          </form>
        </div>
      </FetchStates>
    </BasicDrawer>
  );
}
